import PropTypes from "prop-types";
import moment from "moment-timezone";
import { useUser } from "@context/contexts";
import { useSubscription } from "@hooks/useSubscription";
import { parseSubscriptionEnd, formatDate } from "@utils/index";
import SubscriptionSkeleton from "./SubscriptionSkeleton";
import "@css/Subscription.css";

const HTTP_503_FEATURE_DISABLED = 503;

const SubscriptionErrorState = ({ error }) => {
    const message = error?.status === HTTP_503_FEATURE_DISABLED
        ? "Состояние оплаты сейчас недоступно."
        : "Ошибка при загрузке информации о подписке";

    return (
        <div className="subscription-error">
            <p className="subscription-error-text">
                {message}
            </p>
        </div>
    );
};

SubscriptionErrorState.propTypes = {
    error: PropTypes.shape({
        status: PropTypes.number,
    }),
};

const statusLabels = {
    active: "Активна",
    expired: "Истекла",
    none: "Не активирована",
};

const tierLabels = {
    monthly: "Ежемесячно",
    yearly: "Ежегодно",
};

const resolveStatus = (explicitStatus, premiumUntil) => {
    if (explicitStatus) {
        return explicitStatus;
    }
    if (!premiumUntil) {
        return "none";
    }
    return premiumUntil.isAfter(moment()) ? "active" : "expired";
};

/**
 * SubscriptionStatus - Display current subscription info (status, tier, expiry, auto-renew)
 */
const SubscriptionStatus = ({ enabled = true }) => {
    const { userData } = useUser();
    const { subscription, isLoading, error } = useSubscription({ enabled });

    const fallbackPremiumUntil = parseSubscriptionEnd(userData?.subscription);

    if (!enabled) {
        return null;
    }

    if (isLoading && !fallbackPremiumUntil) {
        return <SubscriptionSkeleton />;
    }

    if (error && !fallbackPremiumUntil) {
        return <SubscriptionErrorState error={error} />;
    }

    const premiumUntil = subscription?.premium_until
        ? moment.unix(subscription.premium_until)
        : fallbackPremiumUntil;

    const status = resolveStatus(subscription?.status, premiumUntil);
    const tier = subscription?.tier ?? null;
    const autoRenew = Boolean(subscription?.auto_renew);
    const paymentMethod = subscription?.payment_method ?? null;
    let autoRenewLabel = "Выключено";
    if (autoRenew) {
        autoRenewLabel = paymentMethod
            ? `Включено (карта •••• ${paymentMethod})`
            : "Включено";
    }

    return (
        <div className="subscription-status-section">
            <div className="subscription-status-header">
                <span className="subscription-status-title">
                    Текущая подписка
                </span>
                <span className={`subscription-badge ${status}`}>
                    {statusLabels[status] || status}
                </span>
            </div>

            {tier ? (
                <div className="subscription-info-row">
                    <span className="subscription-info-label">Тип подписки:</span>
                    <span className="subscription-info-value">
                        {tierLabels[tier] || tier}
                    </span>
                </div>
            ) : null}

            {premiumUntil ? (
                <div className="subscription-info-row">
                    <span className="subscription-info-label">
                        {status === "active" ? "Активна до:" : "Истекла:"}
                    </span>
                    <span className="subscription-info-value">
                        {formatDate(premiumUntil.toDate())}
                    </span>
                </div>
            ) : null}

            <div className="subscription-info-row">
                <span className="subscription-info-label">Автопродление:</span>
                <span className="subscription-info-value">
                    {autoRenewLabel}
                </span>
            </div>

            {!autoRenew && status === "expired" ? (
                <div className="subscription-warning">
                    <p className="subscription-warning-text">
                        Подписка истекла. Включите автопродление ниже.
                    </p>
                </div>
            ) : null}
        </div>
    );
};

SubscriptionStatus.propTypes = {
    enabled: PropTypes.bool,
};

export default SubscriptionStatus;
